import type { UserManagerSettings } from 'oidc-client-ts';

/**
 * Query parameters Cognito needs on its `/logout` endpoint.
 *
 * Cognito's end-session endpoint is not standard OIDC: it ignores the
 * `id_token_hint` + `post_logout_redirect_uri` pair oidc-client-ts sends and
 * requires either `client_id` + `logout_uri`, or `client_id` + `redirect_uri`
 * + `response_type`. Given neither, it rejects the request, the hosted-UI
 * session cookie survives, and the next sign-in is granted silently — so
 * clearing local tokens never actually logs anyone out.
 *
 * By default we use the `redirect_uri` variant: that URL is already
 * registered as a callback on the app client, whereas a sign-out URL for
 * `logout_uri` may not be — and a `redirect_mismatch` here would strand the
 * user on a Cognito error page. It lands them on Cognito's login form.
 *
 * An app with its own sign-in page passes `logoutUri` to come back to it
 * instead; that URL must be listed as a sign-out URL on the app client.
 */
export const cognitoLogoutParams = (
  settings: Pick<
    UserManagerSettings,
    'client_id' | 'redirect_uri' | 'response_type'
  >,
  logoutUri?: string
): Record<string, string> =>
  logoutUri
    ? { client_id: settings.client_id, logout_uri: logoutUri }
    : {
        client_id: settings.client_id,
        redirect_uri: settings.redirect_uri,
        response_type: settings.response_type ?? 'code',
      };
